// frontend/src/pages/SetupAccount.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, ArrowLeft, CheckCircle, Loader2, User, HelpCircle, Lock, Eye, EyeOff } from 'lucide-react';
import PillToggle from '../components/UI/PillToggle';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import weplLogo from '../assets/images/wepl_logo.png';

const SetupAccount = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedIdentifier, setSubmittedIdentifier] = useState('');
  const [error, setError] = useState(null);
  const [inputType, setInputType] = useState('email'); // 'email' or 'staffId'
  const [showPassword, setShowPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    clearErrors,
  } = useForm();

  const identifier = watch('identifier', '');
  const password = watch('password', '');

  // Redirect to reset-password if uid and token are present in URL
  // This handles the case when user clicks the setup link from welcome email
  useEffect(() => {
    const uid = searchParams.get('uid');
    const token = searchParams.get('token');
    if (uid && token) {
      // Redirect to reset-password page which handles password setting
      navigate(`/reset-password?uid=${uid}&token=${token}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // Clear validation errors when switching input types
  useEffect(() => {
    clearErrors('identifier');
    if (identifier) {
      trigger('identifier');
    }
  }, [inputType, clearErrors, trigger, identifier]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      if (inputType === 'email') {
        // Email flow: send account setup welcome email
        const payload = { email: data.identifier.trim().toLowerCase() };
        await authAPI.requestSetupAccount(payload);
        setSubmittedIdentifier(data.identifier);
        setIsSubmitted(true);
      } else {
        // Staff ID flow: login directly with computed password
        const staffId = data.identifier.trim().toUpperCase();
        const result = await login(staffId, data.password, false);
        
        if (result.success) {
          // Login successful - redirect will happen automatically via AuthContext
          navigate('/');
        } else {
          setError(result.error || 'Invalid Staff ID or password. Please check your credentials.');
        }
      }
    } catch (err) {
      if (inputType === 'email') {
        // Even on error, show success message to prevent enumeration
        setSubmittedIdentifier(data.identifier);
        setIsSubmitted(true);
      } else {
        setError(err.response?.data?.detail || 'Login failed. Please check your Staff ID and password.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mask email for display
  const maskEmail = (email) => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const maskedLocal = local.length > 2 
      ? local[0] + '*'.repeat(Math.min(local.length - 2, 5)) + local[local.length - 1]
      : local;
    return `${maskedLocal}@${domain}`;
  };

  // Mask Staff ID for display (show first 2 and last 2 characters)
  const maskStaffId = (staffId) => {
    if (!staffId || staffId.length < 4) return staffId;
    return staffId.substring(0, 2) + '*'.repeat(staffId.length - 4) + staffId.substring(staffId.length - 2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-28 h-28 sm:w-36 sm:h-36 flex items-center justify-center overflow-hidden">
              <img 
                src={weplLogo} 
                alt="Company Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">
            {isSubmitted ? 'Check Your Email' : 'Welcome to HR Portal'}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {isSubmitted 
              ? 'We\'ve sent you a password setup link to your email.'
              : 'Set up your account to access the HR Portal. Enter your work email or Staff ID to get started.'
            }
          </p>
        </div>

        {/* Form / Success Message */}
        <div className="bg-white rounded-xl shadow-soft p-8">
          {isSubmitted ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              
              <p className="text-gray-700 mb-2">
                If an account exists for
              </p>
              <p className="font-medium text-gray-900 mb-4">
                {inputType === 'email' ? maskEmail(submittedIdentifier) : maskStaffId(submittedIdentifier)}
              </p>
              <p className="text-sm text-gray-600 mb-6">
                you will receive an email with a link to set up your password.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>What's next?</strong>
                </p>
                <ol className="text-sm text-blue-700 text-left space-y-1 list-decimal list-inside">
                  <li>Check your email inbox (and spam folder)</li>
                  <li>Click the password setup link in the email</li>
                  <li>Create a secure password</li>
                  <li>Log in to access the HR Portal</li>
                </ol>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Didn't receive the email?</strong>
                  <br />
                  Check your spam folder or contact your HR administrator for assistance.
                </p>
              </div>

              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Go to Login
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Input Type Toggle */}
              <PillToggle
                value={inputType}
                onChange={setInputType}
                options={[
                  { id: 'email', label: 'Work Email', icon: Mail },
                  { id: 'staffId', label: 'Staff ID', icon: User },
                ]}
                variant="text"
                theme="employee"
                fullWidth
                ariaLabel="Login method"
              />

              {/* Identifier Field */}
              <div>
                <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-2">
                  {inputType === 'email' ? 'Work Email' : 'Staff ID'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {inputType === 'email' ? (
                      <Mail className="h-5 w-5 text-gray-400" />
                    ) : (
                      <User className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <input
                    {...register('identifier', {
                      required: `${inputType === 'email' ? 'Email' : 'Staff ID'} is required`,
                      validate: (value) => {
                        if (inputType === 'email') {
                          const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                          return emailPattern.test(value) || 'Please enter a valid email address';
                        }
                        return true; // No validation for Staff ID
                      },
                    })}
                    type={inputType === 'email' ? 'email' : 'text'}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.identifier ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={inputType === 'email' ? 'Enter your work email' : 'Enter your Staff ID (e.g., S0123)'}
                    autoComplete={inputType === 'email' ? 'email' : 'username'}
                  />
                </div>
                {errors.identifier && (
                  <p className="mt-1 text-sm text-red-600">{errors.identifier.message}</p>
                )}
                {inputType === 'staffId' && identifier && (
                  <p className="mt-1 text-xs text-gray-500">
                    Don't know your Staff ID? Use your work email instead.
                  </p>
                )}
              </div>

              {/* Password Field - Only shown when Staff ID is selected */}
              {inputType === 'staffId' && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters',
                        },
                      })}
                      type={showPassword ? 'text' : 'password'}
                      className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                  
                  {/* Password Helper Text */}
                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800 mb-2">
                      <strong>First-time login password format:</strong>
                    </p>
                    <p className="text-xs text-blue-700 mb-1">
                      Last 4 characters of your NRIC/FIN + Date of Birth (DDMMYYYY)
                    </p>
                    <p className="text-xs text-blue-600 italic">
                      Example: If NRIC is <strong>S1234567A</strong> and DOB is <strong>15 May 1985</strong>, password is: <strong>567A15051985</strong>
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary w-full h-12 text-base flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {inputType === 'email' ? 'Sending Setup Link...' : 'Signing in...'}
                  </>
                ) : (
                  inputType === 'email' ? 'Send Setup Link' : 'Sign In'
                )}
              </button>

              {/* Help Section */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start gap-2">
                  <HelpCircle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-1">Need Help?</p>
                    {inputType === 'email' ? (
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>• Don't know your email? Use your Staff ID instead</li>
                        <li>• Don't know your Staff ID? Contact your HR administrator</li>
                        <li>• Didn't receive the email? Check your spam folder</li>
                      </ul>
                    ) : (
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>• Password: Last 4 chars of NRIC/FIN + DOB (DDMMYYYY)</li>
                        <li>• Don't know your password? Use email to receive setup link</li>
                        <li>• Need assistance? Contact your HR administrator</li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Back to Login */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Already have an account? Sign in
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>
            Need assistance? Contact your HR administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupAccount;

